import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuX } from "react-icons/lu";
import IdealStudio from "../../../../../core/editor/main";
import { isSlide, isContainedView } from "../../../../../common/utils";
import { ID_PREFIX_BOX } from "../../../../../common/constants";
import { randomPositionGenerator } from "../../../clipboard/clipboard.utils";
import { createBox } from "../../../../../common/common_tools";
import getMediaBoxSize from "../../../../utils/getMediaBoxSize";
import AppModal from "../../../common/modal/AppModal";
import PanelSearch from "../ui/PanelSearch";
import AssetGrid from "../ui/AssetGrid";
import AssetCell from "../ui/AssetCell";
import EmptyState from "../ui/EmptyState";
import "./_panel.scss";

// Image-asset panel for the Components module (Animations / Icons / Characters):
// each inserts a HotspotImages box; data + grid sizing come from props.
function ImageItemsPanel({
  items,
  cellSize,
  ratio,
  componentType,
  addLabel,
  previewLabel,
  onElementSelected,
  onBoxAdded,
  boxes,
  navItems,
  navItemSelected,
  containedViews,
  containedViewSelected,
  fileModalResult,
  onClose,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [previewFile, setPreviewFile] = useState(null);

  const filteredItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        (item.keywords && item.keywords.toLowerCase().includes(q))
    );
  }, [searchTerm, items]);

  const handleAddToScene = async (item) => {
    const url = item.url;

    const isReplaceMode = fileModalResult && fileModalResult.id;
    const acceptsImage =
      !fileModalResult?.accept || fileModalResult.accept.toLowerCase().includes("image");
    if (isReplaceMode && acceptsImage) {
      if (onElementSelected && url) onElementSelected(item.name, url, "image", item.id);
      return;
    }

    if (onBoxAdded && url && boxes && navItems && containedViews) {
      const pluginName = "HotspotImages";
      const plugin = IdealStudio.Plugins.get(pluginName);
      if (plugin) {
        const navItem =
          containedViewSelected && containedViews[containedViewSelected]
            ? containedViews[containedViewSelected]
            : navItems[navItemSelected];

        if (navItem) {
          const cv =
            containedViewSelected &&
            containedViews[containedViewSelected] &&
            isContainedView(containedViewSelected);
          const cvslide = cv && isSlide(navItem.type);
          const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
          const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

          const { width, height, widthUnit, heightUnit } = await getMediaBoxSize(url, "image");

          const initialParams = {
            parent,
            container: 0,
            name: pluginName,
            position: {
              x: randomPositionGenerator(10, 30),
              y: randomPositionGenerator(10, 30),
              type: "absolute",
            },
            id: ID_PREFIX_BOX + Date.now(),
            page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
            url,
            componentType,
            width,
            height,
            widthUnit,
            heightUnit,
          };

          createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
        }
      }
    }

    if (onElementSelected) {
      onElementSelected(item.name, item.url, "image", item.id);
    }
  };

  return (
    <div className="cmp-panel">
      <div className="cmp-panel__toolbar">
        <PanelSearch
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={() => setSearchTerm("")}
          placeholder={i18n.t("search") || "Search"}
        />
        {onClose && (
          <button
            type="button"
            className="cmp-panel__close"
            onClick={onClose}
            title={i18n.t("close")}
          >
            <LuX size={15} />
          </button>
        )}
      </div>

      <div className="cmp-panel__scroll">
        {filteredItems.length === 0 ? (
          <EmptyState title={i18n.t("no_results")} hint={i18n.t("try_other_search")} />
        ) : (
          <AssetGrid cellSize={cellSize} gap={10}>
            {filteredItems.map((item) => (
              <AssetCell
                key={item.id}
                dragProps={{
                  className: "palette-item floatingEditorBox",
                  name: "HotspotImages",
                  "data-plugin": "HotspotImages",
                  "data-url": item.url,
                  "data-media": "image",
                  "data-type": componentType,
                }}
                ratio={ratio}
                title={item.name}
                onPreview={() =>
                  setPreviewFile({ id: item.id, type: "image", url: item.url, name: item.name })
                }
                onAdd={() => handleAddToScene(item)}
                previewLabel={previewLabel || i18n.t("preview")}
                addLabel={addLabel}
              >
                <img src={item.url} alt={item.name} />
              </AssetCell>
            ))}
          </AssetGrid>
        )}
      </div>

      {previewFile && (
        <AppModal
          show
          onClose={() => setPreviewFile(null)}
          title={previewFile.name}
          centered
          size="lg"
          showSceneToggle={false}
          className="libraryPreviewModal"
        >
          <img className="cmp-panel__preview" src={previewFile.url} alt={previewFile.name} />
        </AppModal>
      )}
    </div>
  );
}

ImageItemsPanel.propTypes = {
  items: PropTypes.array.isRequired,
  cellSize: PropTypes.number,
  ratio: PropTypes.number,
  componentType: PropTypes.string,
  addLabel: PropTypes.string,
  previewLabel: PropTypes.string,
  onElementSelected: PropTypes.func,
  onBoxAdded: PropTypes.func,
  boxes: PropTypes.object,
  navItems: PropTypes.object,
  navItemSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containedViews: PropTypes.object,
  containedViewSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fileModalResult: PropTypes.object,
  onClose: PropTypes.func,
};

ImageItemsPanel.defaultProps = {
  cellSize: 84,
  ratio: 1,
};

export default ImageItemsPanel;
